import React from 'react';
import DropdownMenu from '../DropdownMenu';
import DateSelector from '../DateSelector';
import dayjs from 'dayjs';
import { useClassInsights } from './ClassInsightsContext';
import { withDateDefaults } from './useFilters';
import { ALL_OPTION } from '../../utils/urlParams';
import {
  catalogClasses,
  selectedClasses,
  selectedSubjects,
  selectedTaskTypes,
  deriveSubjectOptions,
  deriveTaskTypeOptions,
  deriveTaskOptions,
  deriveStudentOptions,
  markingToolsInScope,
} from './catalogSelectors';

const isShowAll = (ids) => Array.isArray(ids) && ids.length === 1 && ids[0] === ALL_OPTION;

function getMarkingToolDropdownConfig(label) {
  const normalized = label.toLowerCase();
  if (normalized.includes('rubric')) {
    return {
      dropdownHeader: 'Rubrics',
      dropdownSubtitle: 'Select rubric to show data for',
      previewType: 'rubric',
    };
  }
  if (normalized.includes('success')) {
    return {
      dropdownHeader: 'Success criteria',
      dropdownSubtitle: 'Select success criteria to show data for',
      previewType: 'successcriteria',
    };
  }
  if (normalized.includes('short') || normalized.includes('answer')) {
    return {
      dropdownHeader: 'Short Answers',
      dropdownSubtitle: 'Select short answer to show data for',
      previewType: 'shortanswer',
    };
  }
  return {
    dropdownHeader: 'Specific comments',
    dropdownSubtitle: 'Select specific comment to show data for',
    previewType: 'commentbank',
  };
}

export function ModalFilters({
  firstFilterLabel,
  firstFilterOptions,
  firstFilterValue,
  firstFilterOnChange,
  firstFilterGetOptionLabel,
  firstFilterGetOptionValue,
  filters,
  setFilters,
  firstFilterShowAllOption = false,
  firstFilterSingleSelect = false,
  firstFilterShowCheckboxes = true,
  firstFilterShowAllWhenAllSelected = false,
}) {
  const { catalog } = useClassInsights();
  const dates = withDateDefaults(filters);

  const widenToAllClasses = (f) =>
    !f.classIds || f.classIds.length === 0
      ? { ...f, classIds: catalogClasses(catalog).map((c) => String(c.id)) }
      : f;

  // Helper to validate marking tool IDs against current scope
  const validateMarkingToolIds = (newFilters) => {
    // JED-1066: empty classIds means "all classes", so resolve tools against
    // all catalog classes -- otherwise every filter change in the "all" state
    // would clear the teacher's tool selections.
    const toolsInScope = markingToolsInScope(catalog, widenToAllClasses(newFilters));

    return {
      commentBankIds: (filters.commentBankIds || []).filter((id) =>
        toolsInScope.commentBanks.some((opt) => String(opt.id) === String(id))
      ),
      successCriteriaIds: isShowAll(filters.successCriteriaIds)
        ? filters.successCriteriaIds
        : (filters.successCriteriaIds || []).filter((id) =>
            toolsInScope.successCriteria.some((opt) => String(opt.id) === String(id))
          ),
      rubricIds: (filters.rubricIds || []).filter((id) =>
        toolsInScope.rubrics.some((opt) => String(opt.id) === String(id))
      ),
      shortAnswerIds: (filters.shortAnswerIds || []).filter((id) =>
        toolsInScope.shortAnswers.some((opt) => String(opt.id) === String(id))
      ),
    };
  };

  const classes = catalogClasses(catalog);
  // JED-1066: an empty class selection means "all classes". Widen before
  // deriving the dependent dropdowns, the same way Header does, so the
  // expanded report's filters aren't empty while it shows all-class data.
  const scopeFilters = widenToAllClasses(filters);
  const selectedClassesData = selectedClasses(catalog, scopeFilters);
  const subjectOptions = deriveSubjectOptions(selectedClassesData);
  const subjects = selectedSubjects(selectedClassesData, scopeFilters);
  const taskTypes = selectedTaskTypes(subjects, scopeFilters);
  const taskTypeOptions = deriveTaskTypeOptions(subjects).map((opt) => opt.label);
  const taskOptions = deriveTaskOptions(taskTypes);
  const studentOptions = deriveStudentOptions(selectedClassesData);

  // Show subject filter when multiple classes selected OR when class has multiple subjects
  const showSubjectFilter =
    (filters.classIds && filters.classIds.length > 1) ||
    subjectOptions.length > 1;

  const markingToolDropdown = getMarkingToolDropdownConfig(firstFilterLabel);
  return (
    <div className="ci-modal-filters-grid">
      <div className="ci-modal-filter-group">
        <label>{firstFilterLabel}</label>
        <DropdownMenu
          multiselect
          singleSelect={firstFilterSingleSelect}
          options={firstFilterOptions}
          selectedValues={firstFilterValue}
          onChange={firstFilterOnChange}
          placeholder={`All ${firstFilterLabel.toLowerCase()}`}
          getOptionLabel={firstFilterGetOptionLabel}
          getOptionValue={firstFilterGetOptionValue}
          alwaysWhite
          showAllOption={firstFilterShowAllOption}
          showAllWhenAllSelected={
            firstFilterShowAllWhenAllSelected || firstFilterLabel.toLowerCase().includes('success')
          }
          showCheckboxes={firstFilterShowCheckboxes}
          showSearch
          compactDisplay
          triggerFontSize="14px"
          triggerPadding="10px 12px"
          dropdownHeader={markingToolDropdown.dropdownHeader}
          dropdownSubtitle={markingToolDropdown.dropdownSubtitle}
          showPreviewIcon={true}
          previewType={markingToolDropdown.previewType}
        />
      </div>

      <div className="ci-modal-filter-group">
        <label>Classes</label>
        <DropdownMenu
          multiselect
          options={classes.map((c) => String(c.id))}
          selectedValues={filters.classIds}
          onChange={(values) => {
            const allClassIds = classes.map((c) => String(c.id));
            const nextClassIds = values.length === 0 ? allClassIds : values;
            const newFilters = {
              ...filters,
              classIds: nextClassIds,
              subjectCodes: [],
              taskTypes: [],
              taskIds: [],
              studentIds: [],
            };
            setFilters({
              ...newFilters,
              ...validateMarkingToolIds(newFilters),
            });
          }}
          listAllWhenEmpty
          placeholder="All classes"
          getOptionLabel={(id) => {
            const classItem = classes.find((c) => String(c.id) === String(id));
            return classItem.title || classItem.name;
          }}
          getOptionValue={(id) => id}
          alwaysWhite
          showSearch
          showCheckboxes
          showAllOption
          compactDisplay
          triggerFontSize="14px"
          triggerPadding="10px 12px"
          dropdownHeader="Classes"
          dropdownSubtitle="Select classes to show data for"
        />
      </div>

      {showSubjectFilter && (
        <div className="ci-modal-filter-group">
          <label>Subject</label>
          <DropdownMenu
            multiselect
            options={subjectOptions}
            selectedValues={filters.subjectCodes}
            onChange={(values) => {
              const newFilters = {
                ...filters,
                subjectCodes: values,
                taskTypes: [],
                taskIds: [],
              };
              setFilters({
                ...newFilters,
                ...validateMarkingToolIds(newFilters),
              });
            }}
            placeholder="All subjects"
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.id}
            alwaysWhite
            showSearch
            showCheckboxes
            showAllOption
            compactDisplay
            triggerFontSize="14px"
            triggerPadding="10px 12px"
            dropdownHeader="Subject"
            dropdownSubtitle="Select subjects to show data for"
          />
        </div>
      )}

      <div className="ci-modal-filter-group">
        <label>Task type</label>
        <DropdownMenu
          multiselect
          options={taskTypeOptions}
          selectedValues={filters.taskTypes}
          onChange={(values) => {
            const newFilters = { ...filters, taskTypes: values, taskIds: [] };
            setFilters({
              ...newFilters,
              ...validateMarkingToolIds(newFilters),
            });
          }}
          placeholder="All task types"
          getOptionLabel={(opt) => opt}
          getOptionValue={(opt) => opt}
          alwaysWhite
          showSearch
          showCheckboxes
          showAllOption
          compactDisplay
          triggerFontSize="14px"
          triggerPadding="10px 12px"
          dropdownHeader="Task type"
          dropdownSubtitle="Select task types to show data for"
        />
      </div>

      <div className="ci-modal-filter-group">
        <label>Task name</label>
        <DropdownMenu
          multiselect
          options={taskOptions}
          selectedValues={filters.taskIds}
          onChange={(values) => {
            const newFilters = { ...filters, taskIds: values };
            setFilters({
              ...newFilters,
              ...validateMarkingToolIds(newFilters),
            });
          }}
          placeholder="All tasks"
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.id}
          alwaysWhite
          showSearch
          showCheckboxes
          showAllOption
          compactDisplay
          triggerFontSize="14px"
          triggerPadding="10px 12px"
          dropdownHeader="Task name"
          dropdownSubtitle="Select tasks to show data for"
        />
      </div>

      <div className="ci-modal-filter-group">
        <label>Students</label>
        <DropdownMenu
          multiselect
          options={studentOptions}
          selectedValues={filters.studentIds}
          onChange={(values) => setFilters({ studentIds: values })}
          placeholder="All students"
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.id}
          alwaysWhite
          showSearch
          showCheckboxes
          showAllOption
          compactDisplay
          triggerFontSize="14px"
          triggerPadding="10px 12px"
          dropdownHeader="Students"
          dropdownSubtitle="Select students to show data for"
        />
      </div>

      <div className="ci-modal-filter-group">
        <label>From</label>
        <DateSelector
          value={dayjs(dates.startDate)}
          onChange={(newValue) => setFilters({ startDate: newValue.format('YYYY-MM-DD') })}
          dateOnly
          matchDropdownStyle
          minDate={null}
        />
      </div>

      <div className="ci-modal-filter-group">
        <label>To</label>
        <DateSelector
          value={dayjs(dates.endDate)}
          onChange={(newValue) => setFilters({ endDate: newValue.format('YYYY-MM-DD') })}
          dateOnly
          matchDropdownStyle
          minDate={null}
        />
      </div>
    </div>
  );
}
